import { useState } from "react";
import { motion } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { FadeUp, RevealLineInView } from "@/components/animations/Reveal";

// Placeholder reviews — kann jederzeit mit echten Google-Rezensionen ersetzt werden.
const REVIEWS = {
    de: {
        chapter: "Kapitel 04",
        eyebrow: "Google Rezensionen",
        title_1: "Was unsere",
        title_2: "Kundinnen sagen",
        sub: "Auszüge aus echten Rückmeldungen — vielen Dank für Ihr Vertrauen.",
        cta: "Alle Google Rezensionen ansehen",
        items: [
            { name: "Maja S.", date: "vor 1 Woche", text: "Seit über einem Jahr Stammkundin bei Tijana — jedes Mal eine neue Begeisterung. Die Beratung ist ehrlich und das Ergebnis spricht für sich. Mein Blond sieht aus wie frisch aus einem Magazin." },
            { name: "Vanessa H.", date: "vor 2 Wochen", text: "Super moderner Salon, sehr ruhige Atmosphäre und man wird mit einem Kaffee empfangen. Mein Keratin hält bereits seit 4 Monaten perfekt. Preis-Leistung top." },
            { name: "Milena Đ.", date: "vor 1 Monat", text: "Zur Hochzeit meiner Schwester hat Tijana die Brautfrisur gemacht — hielt den ganzen Abend, war elegant und genau nach Wunsch. Alle Gäste haben gefragt, wer das war!" },
            { name: "Sanja I.", date: "vor 2 Monaten", text: "Habe lange nach einem Salon gesucht, der mit Keune arbeitet und ausserdem Tape-In Extensions macht. Hier passt beides. Sehr professionell — man merkt, dass Tijana das wirklich kann." },
            { name: "Marie K.", date: "vor 3 Monaten", text: "Ich wollte etwas Natürliches und bekam genau das — ein warmes Balayage, das zu meinem Teint passt. Kein stundenlanges Blättern durch Pinterest nötig, Tijana weiß, was sie tut." },
            { name: "Lara F.", date: "vor 3 Monaten", text: "Mädchenschnitt für meine Tochter ist jedes Mal eine Freude — Tijana ist super geduldig mit Kindern. Danke für die liebevolle Betreuung." },
        ],
    },
    bs: {
        chapter: "Poglavlje 04",
        eyebrow: "Google recenzije",
        title_1: "Šta kažu",
        title_2: "naše klijentkinje",
        sub: "Izvodi iz stvarnih poruka — hvala vam na povjerenju.",
        cta: "Pogledaj sve Google recenzije",
        items: [
            { name: "Maja S.", date: "prije 1 sedmicu", text: "Više od godinu dana sam stalna klijentkinja kod Tijane — svaki put sam oduševljena iznova. Savjeti su iskreni, a rezultat govori sam za sebe. Moje plavo izgleda kao sa naslovnice magazina." },
            { name: "Vanessa H.", date: "prije 2 sedmice", text: "Super moderan salon, mirna atmosfera i doček uz kafu. Moj keratin traje već 4 mjeseca savršeno. Odličan odnos cijene i kvaliteta." },
            { name: "Milena Đ.", date: "prije 1 mjesec", text: "Za sestrinu svadbu je Tijana napravila mladinu frizuru — izdržala je cijelu veče, bila je elegantna i baš po želji. Svi gosti su pitali ko je radio!" },
            { name: "Sanja I.", date: "prije 2 mjeseca", text: "Dugo sam tražila salon koji radi s Keuneom i koji pored toga radi i Tape-In extensions. Ovdje imate oboje. Vrlo profesionalno — vidi se da Tijana zaista to zna." },
            { name: "Marie K.", date: "prije 3 mjeseca", text: "Htjela sam nešto prirodno i dobila sam baš to — topli balayage koji odgovara mom tenu. Nema potrebe satima gledati Pinterest, Tijana zna šta radi." },
            { name: "Lara F.", date: "prije 3 mjeseca", text: "Dječije šišanje za moju kćerkicu je uvijek radost — Tijana je super strpljiva s djecom. Hvala na brizi s puno topline." },
        ],
    },
    en: {
        chapter: "Chapter 04",
        eyebrow: "Google Reviews",
        title_1: "What our",
        title_2: "clients say",
        sub: "Excerpts from real feedback — thank you for your trust.",
        cta: "See all Google reviews",
        items: [
            { name: "Maja S.", date: "1 week ago", text: "I've been a regular with Tijana for over a year — every visit is a new wow moment. Honest advice and the result speaks for itself. My blonde looks fresh out of a magazine." },
            { name: "Vanessa H.", date: "2 weeks ago", text: "Very modern salon, calm atmosphere, and you're welcomed with a coffee. My keratin has held perfectly for 4 months now. Great value for the price." },
            { name: "Milena Đ.", date: "1 month ago", text: "For my sister's wedding Tijana did the bridal hair — it held all night long, elegant and exactly what we wanted. All the guests asked who did it!" },
            { name: "Sanja I.", date: "2 months ago", text: "I'd been looking for a salon that works with Keune and also does Tape-In extensions. This place has both. Very professional — you can tell Tijana knows her craft." },
            { name: "Marie K.", date: "3 months ago", text: "I wanted something natural and got exactly that — a warm balayage that matches my skin tone. No hours of Pinterest scrolling needed, Tijana knows what she's doing." },
            { name: "Lara F.", date: "3 months ago", text: "The girls' cut for my daughter is always a pleasure — Tijana is super patient with kids. Thanks for the warm, caring service." },
        ],
    },
    it: {
        chapter: "Capitolo 04",
        eyebrow: "Recensioni Google",
        title_1: "Cosa dicono le",
        title_2: "nostre clienti",
        sub: "Estratti da feedback reali — grazie per la fiducia.",
        cta: "Vedi tutte le recensioni Google",
        items: [
            { name: "Maja S.", date: "1 settimana fa", text: "Cliente fissa di Tijana da oltre un anno — ogni volta un nuovo entusiasmo. Consigli sinceri e il risultato parla da sé. Il mio biondo sembra uscito da una rivista." },
            { name: "Vanessa H.", date: "2 settimane fa", text: "Salone molto moderno, atmosfera tranquilla e si viene accolte con un caffè. La mia cheratina dura perfettamente da 4 mesi. Qualità-prezzo top." },
            { name: "Milena Đ.", date: "1 mese fa", text: "Per il matrimonio di mia sorella Tijana ha fatto l'acconciatura della sposa — ha retto tutta la serata, elegante ed esattamente come desideravamo. Tutti hanno chiesto chi fosse la parrucchiera!" },
            { name: "Sanja I.", date: "2 mesi fa", text: "Cercavo da tempo un salone che lavorasse con Keune e che facesse anche Tape-In. Qui c'è entrambe. Molto professionale — si vede che Tijana sa il fatto suo." },
            { name: "Marie K.", date: "3 mesi fa", text: "Volevo qualcosa di naturale e ho avuto proprio quello — un balayage caldo che si abbina alla mia pelle. Niente ore su Pinterest, Tijana sa cosa fare." },
            { name: "Lara F.", date: "3 mesi fa", text: "Il taglio per mia figlia è sempre un piacere — Tijana è super paziente con i bambini. Grazie per la gentilezza." },
        ],
    },
    fr: {
        chapter: "Chapitre 04",
        eyebrow: "Avis Google",
        title_1: "Ce que disent",
        title_2: "nos clientes",
        sub: "Extraits d'avis réels — merci pour votre confiance.",
        cta: "Voir tous les avis Google",
        items: [
            { name: "Maja S.", date: "il y a 1 semaine", text: "Cliente fidèle de Tijana depuis plus d'un an — chaque fois un nouvel enthousiasme. Conseils honnêtes et le résultat parle de lui-même. Mon blond est digne d'un magazine." },
            { name: "Vanessa H.", date: "il y a 2 semaines", text: "Salon très moderne, ambiance calme, et on est accueillie avec un café. Ma kératine tient parfaitement depuis 4 mois. Rapport qualité-prix excellent." },
            { name: "Milena Đ.", date: "il y a 1 mois", text: "Pour le mariage de ma sœur, Tijana a fait la coiffure de mariée — elle a tenu toute la soirée, élégante et exactement comme voulu. Tous les invités ont demandé qui l'avait faite !" },
            { name: "Sanja I.", date: "il y a 2 mois", text: "Je cherchais un salon qui travaille avec Keune et qui fait aussi les extensions Tape-In. Ici, il y a les deux. Très professionnel — Tijana maîtrise vraiment son métier." },
            { name: "Marie K.", date: "il y a 3 mois", text: "Je voulais quelque chose de naturel et j'ai eu exactement cela — un balayage chaud qui va avec ma carnation. Pas besoin d'heures sur Pinterest, Tijana sait ce qu'elle fait." },
            { name: "Lara F.", date: "il y a 3 mois", text: "La coupe de ma fille est toujours un plaisir — Tijana est très patiente avec les enfants. Merci pour la gentillesse." },
        ],
    },
    pl: {
        chapter: "Rozdział 04",
        eyebrow: "Opinie Google",
        title_1: "Co mówią",
        title_2: "nasze klientki",
        sub: "Fragmenty prawdziwych opinii — dziękujemy za zaufanie.",
        cta: "Zobacz wszystkie opinie Google",
        items: [
            { name: "Maja S.", date: "tydzień temu", text: "Stałą klientką Tijany jestem od ponad roku — za każdym razem nowe zachwyty. Szczere doradztwo i efekt mówi sam za siebie. Mój blond wygląda jak z okładki." },
            { name: "Vanessa H.", date: "2 tygodnie temu", text: "Bardzo nowoczesny salon, spokojna atmosfera, witają kawą. Moja keratyna trzyma się idealnie już 4 miesiące. Super stosunek jakości do ceny." },
            { name: "Milena Đ.", date: "miesiąc temu", text: "Na ślub mojej siostry Tijana zrobiła fryzurę ślubną — wytrzymała cały wieczór, elegancka i dokładnie tak jak chciałyśmy. Wszyscy pytali kto to robił!" },
            { name: "Sanja I.", date: "2 miesiące temu", text: "Długo szukałam salonu, który pracuje z Keune i do tego robi Tape-In. Tu jest jedno i drugie. Bardzo profesjonalnie — widać, że Tijana się zna." },
            { name: "Marie K.", date: "3 miesiące temu", text: "Chciałam coś naturalnego i dostałam dokładnie to — ciepły balayage pasujący do mojej karnacji. Nie trzeba godzinami przeglądać Pinteresta, Tijana wie co robi." },
            { name: "Lara F.", date: "3 miesiące temu", text: "Strzyżenie mojej córki to zawsze przyjemność — Tijana jest super cierpliwa z dziećmi. Dziękuję za ciepłą obsługę." },
        ],
    },
};

const GOOGLE_REVIEWS_URL = "https://www.google.com/search?q=Frizerka+Hair+by+Tijana+Mannheim";

export default function Reviews() {
    const { lang } = useLanguage();
    const R = REVIEWS[lang] || REVIEWS.de;
    const [index, setIndex] = useState(0);
    const perPage = 3;
    const total = R.items.length;
    const maxIndex = Math.max(0, total - perPage);

    const prev = () => setIndex((i) => Math.max(0, i - 1));
    const next = () => setIndex((i) => Math.min(maxIndex, i + 1));

    return (
        <section id="reviews" className="relative bg-white py-16 md:py-36 overflow-hidden" data-testid="reviews">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                <div className="flex items-center gap-4 mb-8">
                    <span className="chapter-num text-lg">{R.chapter}</span>
                    <span className="h-[1px] flex-1 bg-gold/40" />
                    <span className="eyebrow inline-flex items-center gap-2">
                        <GoogleG className="w-3.5 h-3.5" />
                        {R.eyebrow}
                    </span>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14 items-end">
                    <h2 className="lg:col-span-8 font-display text-ink leading-[0.98] text-4xl sm:text-5xl lg:text-6xl tracking-tight">
                        <RevealLineInView>{R.title_1}</RevealLineInView>{" "}
                        <span className="serif-italic text-gold">
                            <RevealLineInView delay={0.1}>{R.title_2}</RevealLineInView>
                        </span>
                    </h2>
                    <FadeUp delay={0.15} className="lg:col-span-4">
                        <div className="flex items-center gap-4 mb-3">
                            <Stars value={5} />
                            <span className="font-display italic text-2xl text-ink">5.0</span>
                        </div>
                        <p className="text-ink/70 text-sm leading-relaxed">{R.sub}</p>
                    </FadeUp>
                </div>

                <div className="relative">
                    <div className="overflow-hidden">
                        <motion.div
                            className="flex gap-6"
                            animate={{ x: `calc(${-index} * (100% / ${perPage}) - ${index * 24 / perPage}px)` }}
                            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                        >
                            {R.items.map((r, i) => (
                                <article
                                    key={`${r.name}-${i}`}
                                    data-testid={`review-card-${i}`}
                                    className="shrink-0 w-full md:w-[calc((100%-3rem)/2)] lg:w-[calc((100%-3rem)/3)] bg-cream border border-gold/25 rounded-3xl p-6 md:p-10 flex flex-col justify-between min-h-[280px] md:min-h-[320px] relative overflow-hidden"
                                >
                                    <Quote className="absolute -top-2 -right-2 w-24 h-24 text-gold/10" />
                                    <div>
                                        <Stars value={5} />
                                        <p className="mt-6 text-ink text-[0.98rem] leading-relaxed font-sans">
                                            „{r.text}"
                                        </p>
                                    </div>
                                    <div className="mt-8 pt-6 border-t border-gold/20 flex items-center justify-between">
                                        <div>
                                            <div className="font-display italic text-lg text-ink">{r.name}</div>
                                            <div className="text-xs tracking-[0.15em] uppercase text-ink/50 mt-1">{r.date}</div>
                                        </div>
                                        <GoogleG className="w-5 h-5 opacity-80" />
                                    </div>
                                </article>
                            ))}
                        </motion.div>
                    </div>

                    <div className="hidden md:flex items-center justify-between mt-10">
                        <div className="flex items-center gap-2 text-ink/60 text-sm font-display italic">
                            <span className="text-ink">{String(index + 1).padStart(2, "0")}</span>
                            <span className="h-[1px] w-8 bg-gold/50" />
                            <span>{String(total).padStart(2, "0")}</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={prev}
                                disabled={index === 0}
                                aria-label="Previous"
                                data-testid="review-prev"
                                className="w-12 h-12 rounded-full border border-gold/40 text-ink flex items-center justify-center hover:bg-gold hover:text-white hover:border-gold transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <button
                                onClick={next}
                                disabled={index === maxIndex}
                                aria-label="Next"
                                data-testid="review-next"
                                className="w-12 h-12 rounded-full border border-gold/40 text-ink flex items-center justify-center hover:bg-gold hover:text-white hover:border-gold transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                                <ChevronRight className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    <div className="md:hidden flex justify-center gap-2 mt-8">
                        {Array.from({ length: total }).map((_, i) => (
                            <button
                                key={i}
                                onClick={() => setIndex(i)}
                                aria-label={`Go to review ${i + 1}`}
                                className={`h-1.5 rounded-full transition-all ${
                                    i === index ? "w-8 bg-gold" : "w-1.5 bg-gold/30"
                                }`}
                            />
                        ))}
                    </div>
                </div>

                <FadeUp delay={0.1} className="mt-14 flex justify-center">
                    <a
                        href={GOOGLE_REVIEWS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-outline inline-flex items-center gap-3"
                        data-testid="reviews-google-cta"
                    >
                        <GoogleG className="w-4 h-4" />
                        {R.cta}
                    </a>
                </FadeUp>
            </div>
        </section>
    );
}

function Stars({ value }) {
    return (
        <div className="flex items-center gap-1">
            {Array.from({ length: 5 }).map((_, i) => (
                <Star
                    key={i}
                    className={`w-4 h-4 ${i < value ? "text-gold fill-gold" : "text-ink/20"}`}
                    strokeWidth={1.5}
                />
            ))}
        </div>
    );
}

function GoogleG({ className = "w-4 h-4" }) {
    return (
        <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            <path fill="none" d="M0 0h48v48H0z"/>
        </svg>
    );
}
